import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { setOptions, chartConfig } from '../utils/chartConfigs';
import { colors } from '../utils/colors';
import {
  getSumDataSet, getFilteredDataKPI, setDatesAsMonths, groupBy,
  getDataSetGrouped, getDataSetGroupedAvg,
} from '../utils/dataManager';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip, Legend);

const STACKED = true;
const FILL = true;

const now = new Date();
const endDate = new Date(now.getFullYear(), now.getMonth());
const startDate = new Date(now.getFullYear() - 1, now.getMonth() - 1);
const daysSinceStartDate = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

const DashboardKPI = () => {
  const { analyticsData, errors } = useDashboardData();
  const { subscriptionsTrialvsPaid, flagsPerDay, songsPerDay, analyticsEventsPerDay, usersPerDay, subscribersPerDay, usersPerMonth } = analyticsData;

  const flagsFiltered = getFilteredDataKPI(flagsPerDay, daysSinceStartDate);
  const flagsGroupedByMonth = groupBy(setDatesAsMonths(flagsFiltered), 'creation_date');
  const flagsLabels = Object.keys(flagsGroupedByMonth);

  const songsFiltered = getFilteredDataKPI(songsPerDay, daysSinceStartDate);
  const songsGroupedByMonth = groupBy(setDatesAsMonths(songsFiltered), 'creation_date');
  const songsLabels = Object.keys(songsGroupedByMonth);

  const analyticsEventsFiltered = getFilteredDataKPI(analyticsEventsPerDay, daysSinceStartDate);
  const analyticsEventsGroupedByMonth = groupBy(setDatesAsMonths(analyticsEventsFiltered), 'creation_date');
  const analyticsEventsLabels = Object.keys(analyticsEventsGroupedByMonth);

  const usersFiltered = getFilteredDataKPI(usersPerDay, daysSinceStartDate);
  const usersGroupedByMonth = groupBy(setDatesAsMonths(usersFiltered), 'creation_date');
  const usersLabels = Object.keys(usersGroupedByMonth);

  const subscribersFiltered = getFilteredDataKPI(subscribersPerDay, daysSinceStartDate);
  const subscribersGroupedByMonth = groupBy(setDatesAsMonths(subscribersFiltered), 'creation_date');
  const subscribersTimeLabels = Object.keys(subscribersGroupedByMonth);

  const subscribersPaidPlus = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_plus_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_plus_yearly'),
  ]);
  const subscribersPaidSongwriter = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_songwriter_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_songwriter_yearly'),
  ]);
  const subscribersPaidPro = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_pro_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByMonth, 'subscribers_paid_pro_yearly'),
  ]);

  const activeUsersStudio = getDataSetGroupedAvg(subscribersGroupedByMonth, 'active_users_studio');
  const activeUsersSongwriter = getDataSetGroupedAvg(subscribersGroupedByMonth, 'active_users_songwriter');
  const activeUsersExpress = getDataSetGroupedAvg(subscribersGroupedByMonth, 'active_users_express');

  const newStudioSongs = getDataSetGrouped(songsGroupedByMonth, 'appname_studio');
  const newSongwriterSongs = getDataSetGrouped(songsGroupedByMonth, 'appname_songwriter');
  const newExpressSongs = getDataSetGrouped(songsGroupedByMonth, 'source_type_recording');

  const trialSubscriptionPlus = getDataSetGrouped(flagsGroupedByMonth, 'subscription_trial_plus');
  const paidSubscriptionPlus = getDataSetGrouped(flagsGroupedByMonth, 'subscription_paid_plus');
  const trialSubscriptionSongwriter = getDataSetGrouped(flagsGroupedByMonth, 'subscription_trial_songwriter');
  const paidSubscriptionSongwriter = getDataSetGrouped(flagsGroupedByMonth, 'subscription_paid_songwriter');
  const trialSubscriptionPro = getDataSetGrouped(flagsGroupedByMonth, 'subscription_trial_pro');
  const paidSubscriptionPro = getDataSetGrouped(flagsGroupedByMonth, 'subscription_paid_pro');

  const loginSongwriter = getDataSetGrouped(flagsGroupedByMonth, 'login_songwriter');
  const loginSongwriterWin = getDataSetGrouped(flagsGroupedByMonth, 'login_songwriter_win');
  const loginSongwriterOsx = getDataSetGrouped(flagsGroupedByMonth, 'login_songwriter_osx');
  const loginSongwriterOsxAppstore = getDataSetGrouped(flagsGroupedByMonth, 'login_songwriter_osx_appstore');
  const loginStudio = getDataSetGrouped(flagsGroupedByMonth, 'login_studio');
  const loginStudiorWin = getDataSetGrouped(flagsGroupedByMonth, 'login_studio_win');
  const loginStudioOsx = getDataSetGrouped(flagsGroupedByMonth, 'login_studio_osx');
  const loginStudioOsxAppstore = getDataSetGrouped(flagsGroupedByMonth, 'login_studio_osx_appstore');
  const loginExpress = getDataSetGrouped(flagsGroupedByMonth, 'login_express');
  const loginExpressIpad = getDataSetGrouped(flagsGroupedByMonth, 'login_express_ipad');
  const loginExpressIphone = getDataSetGrouped(flagsGroupedByMonth, 'login_express_iphone');
  const loginMyScorecloud = getDataSetGrouped(flagsGroupedByMonth, 'login_my_scorecloud');

  const programStartSongwriter = getDataSetGrouped(analyticsEventsGroupedByMonth, 'program_start');
  const webDownloadSongwriterMac = getDataSetGrouped(analyticsEventsGroupedByMonth, 'web_download_songwriter_mac');
  const webDownloadSongwriterWin = getDataSetGrouped(analyticsEventsGroupedByMonth, 'web_download_songwriter_windows');
  const webDownloadStudioMac = getDataSetGrouped(analyticsEventsGroupedByMonth, 'web_download_studio_mac');
  const webDownloadStudioWin = getDataSetGrouped(analyticsEventsGroupedByMonth, 'web_download_studio_windows');

  const subscriptionChargedSuccessfullyPlusSum = getDataSetGrouped(analyticsEventsGroupedByMonth, 'subscription_charged_successfully_plus_sum');
  const subscriptionChargedSuccessfullyProSum = getDataSetGrouped(analyticsEventsGroupedByMonth, 'subscription_charged_successfully_pro_sum');
  const subscriptionChargedSuccessfullySongwriterSum = getDataSetGrouped(analyticsEventsGroupedByMonth, 'subscription_charged_successfully_songwriter_sum');

  const usersMyScorecloud = getDataSetGrouped(usersGroupedByMonth, 'registration_app_myscorecloud');
  const usersStudio = getDataSetGrouped(usersGroupedByMonth, 'registration_app_studio');
  const usersExpress = getDataSetGrouped(usersGroupedByMonth, 'registration_app_express');
  const usersUnknown = getDataSetGrouped(usersGroupedByMonth, 'registration_app_unknown');
  const usersSongwriter = getDataSetGrouped(usersGroupedByMonth, 'registration_app_songwriter');

  // Express revenue
  const expectedExpressRevenue = [], expectedExpressRevenueIpad = [], expectedExpressRevenueIphone = [];
  for (let i = 0; i < loginExpressIpad.length; i++) {
    expectedExpressRevenue[i] = Math.round(loginExpress[i] * 2.99);
    expectedExpressRevenueIpad[i] = Math.round(loginExpressIpad[i] * 2.99);
    expectedExpressRevenueIphone[i] = Math.round(loginExpressIphone[i] * 2.99);
  }

  const subscriptionsTrialVsPaid = [subscriptionsTrialvsPaid.dataTrial, subscriptionsTrialvsPaid.dataPaid];

  // MAU
  const usersPerMonthKPI = usersPerMonth.data.slice(-14, -1);
  const usersPerMonthTimeLabels = setDatesAsMonths(usersPerMonthKPI).map(d => d.creation_date);
  const usersPerMonthSongwriterValues = [
    usersPerMonthKPI.map(d => parseInt(d.songwriter_start_song_users) || 0),
    usersPerMonthKPI.map(d => parseInt(d.songwriter_finish_song_users) || 0),
  ];
  const usersPerMonthStudioValues = [
    usersPerMonthKPI.map(d => parseInt(d.studio_start_song_users) || 0),
    usersPerMonthKPI.map(d => parseInt(d.studio_finish_song_users) || 0),
  ];

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col">
          <ChartCard header="Current Subscription Count" loading={subscriptionsTrialvsPaid.loading} error={errors.subscriptionsTrialvsPaid}
            values={subscriptionsTrialVsPaid} description="Number of active subscriptions right now" hideAvg>
            <Bar data={chartConfig(subscriptionsTrialvsPaid.subscriptionNames, ['Trial', 'Paid'], subscriptionsTrialVsPaid, [colors.primary, colors.secondary])} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Subscribers over time" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[subscribersPaidPlus, subscribersPaidSongwriter, subscribersPaidPro]} description="Snapshot of active subscriptions over time" hideTotal>
            <Line data={chartConfig(subscribersTimeLabels, ['Paid Plus', 'Paid Songwriter', 'Paid Pro'],
              [subscribersPaidPlus, subscribersPaidSongwriter, subscribersPaidPro], [colors.teal, colors.yellow, colors.purple])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Monthly Active Users - ScoreCloud Songwriter" loading={usersPerMonth.loading} error={errors.usersPerMonth}
            values={usersPerMonthSongwriterValues} description="Unique users per month with important action." hideTotal hideAvg>
            <Line data={chartConfig(usersPerMonthTimeLabels, ['Users with Song Start', 'Users with Song Finish'],
              usersPerMonthSongwriterValues, [colors.teal, colors.yellow])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Monthly Active Users - ScoreCloud Studio" loading={usersPerMonth.loading} error={errors.usersPerMonth}
            values={usersPerMonthStudioValues} description="Unique users per month with important action." hideTotal hideAvg>
            <Line data={chartConfig(usersPerMonthTimeLabels, ['Users with Song Start', 'Users with Song Finish'],
              usersPerMonthStudioValues, [colors.teal, colors.yellow])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="30 Day Action Users (Stacked)" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[activeUsersStudio, activeUsersSongwriter, activeUsersExpress]} hideTotal description="Number of users with any action within 30 days">
            <Line data={chartConfig(subscribersTimeLabels, ['Studio', 'Songwriter', 'Express'],
              [activeUsersStudio, activeUsersSongwriter, activeUsersExpress],
              [colors.variant_lightblue, colors.variant_lightyellow, colors.variant_lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Registered Users per Client (Stacked)" loading={usersPerDay.loading} error={errors.usersPerDay}
            values={[usersMyScorecloud, usersStudio, usersSongwriter, usersExpress, usersUnknown]} description="New registered users by registration source">
            <Line data={chartConfig(usersLabels, ['My Scorecloud', 'Studio', 'Songwriter', 'Express', 'Unknown'],
              [usersMyScorecloud, usersStudio, usersSongwriter, usersExpress, usersUnknown],
              [colors.variant_lightgreen, colors.variant_lightblue, colors.variant_lightyellow, colors.variant_lightred, colors.variant_lightpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Subscriptions, Trial & Paid (Stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[trialSubscriptionPlus, paidSubscriptionPlus, trialSubscriptionSongwriter, paidSubscriptionSongwriter, trialSubscriptionPro, paidSubscriptionPro]}
            description="First time a user starts a specific plan">
            <Line data={chartConfig(flagsLabels, ['Trial Plus', 'Paid Plus', 'Trial Songwriter', 'Paid Songwriter', 'Trial Pro', 'Paid Pro'],
              [trialSubscriptionPlus, paidSubscriptionPlus, trialSubscriptionSongwriter, paidSubscriptionSongwriter, trialSubscriptionPro, paidSubscriptionPro],
              [colors.variant_lightgreen, colors.calm_darkgreen, colors.variant_lightyellow, colors.calm_darkyellow, colors.variant_lightpurple, colors.calm_darkpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="First time login (stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[loginMyScorecloud, loginStudio, loginStudiorWin, loginStudioOsx, loginStudioOsxAppstore, loginSongwriter, loginSongwriterWin, loginSongwriterOsx, loginSongwriterOsxAppstore, loginExpress, loginExpressIpad, loginExpressIphone]}
            description="Number of users logging in to a client for the first time">
            <Line data={chartConfig(flagsLabels,
              ['My Scorecloud', 'Studio', 'Studio Win', 'Studio Osx', 'Studio Osx Appstore', 'Songwriter', 'Songwriter Win', 'Songwriter Osx', 'Songwriter Osx Appstore', 'Express', 'Express iPad', 'Express iPhone'],
              [loginMyScorecloud, loginStudio, loginStudiorWin, loginStudioOsx, loginStudioOsxAppstore, loginSongwriter, loginSongwriterWin, loginSongwriterOsx, loginSongwriterOsxAppstore, loginExpress, loginExpressIpad, loginExpressIphone],
              [colors.grey2, colors.green, colors.darkgreen, colors.teal, colors.darkteal, colors.yellow, colors.darkyellow, colors.orange, colors.darkorange, colors.lightblue, colors.blue, colors.darkblue], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Revenue from successfully charged subscriptions (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[subscriptionChargedSuccessfullyPlusSum, subscriptionChargedSuccessfullySongwriterSum, subscriptionChargedSuccessfullyProSum]}
            description="Per plan (USD from Braintree)">
            <Line data={chartConfig(analyticsEventsLabels, ['Plus (USD)', 'Songwriter (USD)', 'Pro (USD)'],
              [subscriptionChargedSuccessfullyPlusSum, subscriptionChargedSuccessfullySongwriterSum, subscriptionChargedSuccessfullyProSum],
              [colors.calm_darkgreen, colors.calm_darkyellow, colors.calm_darkpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Estimated Revenue From ScoreCloud Express Installs" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone]}
            description="First login in Express × $2.99">
            <Line data={chartConfig(flagsLabels, ['Unknown (USD)', 'iPad (USD)', 'iPhone (USD)'],
              [expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone],
              [colors.lightblue, colors.blue, colors.darkblue], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Songs Saved in the Cloud Per Client (Stacked)" loading={songsPerDay.loading} error={errors.songsPerDay}
            values={[newStudioSongs, newSongwriterSongs, newExpressSongs]}>
            <Line data={chartConfig(songsLabels, ['Studio', 'Songwriter', 'Express'],
              [newStudioSongs, newSongwriterSongs, newExpressSongs],
              [colors.variant_lightblue, colors.variant_lightyellow, colors.variant_lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Songwriter app starts" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[programStartSongwriter]}>
            <Line data={chartConfig(analyticsEventsLabels, ['App starts'], [programStartSongwriter], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Web Downloads (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[webDownloadSongwriterMac, webDownloadSongwriterWin, webDownloadStudioMac, webDownloadStudioWin]}>
            <Line data={chartConfig(analyticsEventsLabels, ['Songwriter Mac', 'Songwriter Win', 'Studio Mac', 'Studio Win'],
              [webDownloadSongwriterMac, webDownloadSongwriterWin, webDownloadStudioMac, webDownloadStudioWin],
              [colors.yellow, colors.darkyellow, colors.teal, colors.darkteal], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
      </div>
    </section>
  );
};

export default DashboardKPI;
